import { useEffect, useState, useCallback } from 'react'
import {
  Box, Stack, Typography, List, ListItemButton, ListItemText, Divider,
  Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Chip, Paper, IconButton, Autocomplete,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material'
import { adminTemplates } from '../../api.js'
import { matchesTemplateSearch } from '../../templateSearch.js'
import DraggableModal from '../../DraggableModal.jsx'

// Preview HTML (decode + newline handling + DOMPurify) — shared with
// SuggestionPanel's template preview; see templatePreview.js.
import { previewHtml, PREVIEW_PARAGRAPH_SX } from '../../templatePreview.js'
import TemplateRichEditor from '../../TemplateRichEditor.jsx'
import { TEAMS, teamLabel, teamForFolder } from '@cxhub/shared'

const EMPTY_FORM = { title: '', content: '', keywords: '', team: null }

// There is no real "folder" entity anywhere (no DB table, no folder-level
// CRUD) — templates.folder is just a flat string like "1A eBay/Refund", and
// the sidebar tree (buildFolderTree below) already treats everything before
// the first "/" as one nesting level. Splitting/joining on the first "/"
// here mirrors that exact convention, so the two pickers below always agree
// with what the sidebar will show once saved.
// A registered account's template folder that has no templates yet: offered
// here so the first template can be filed under it (1A_SUPPORT_EMAIL's
// '1A Support/' and 'AM Support/', 2026-10-06). Any other value can still be typed.
const EMPTY_TEMPLATE_ROOTS = ['1A Support', 'AM Support']

function splitFolder(folder) {
  const i = (folder ?? '').indexOf('/')
  if (i === -1) return { companyChannel: folder ?? '', subFolder: '' }
  return { companyChannel: folder.slice(0, i), subFolder: folder.slice(i + 1) }
}

function TemplateDialog({ open, onClose, onSaved, initial, folders }) {
  const [form, setForm] = useState(EMPTY_FORM)
  // Company & Channel (e.g. "1A eBay") and Folder (e.g. "Refund") are edited
  // as two separate autocompletes — each lists existing values (Folder
  // scoped to whichever Company & Channel is currently picked) but accepts
  // any typed value too (freeSolo), so creating a brand-new folder under an
  // existing company, or a brand-new company entirely, is just typing a
  // name that doesn't appear in the dropdown — no separate "add folder"
  // flow needed, since a folder only ever exists because a template has it.
  const [companyChannel, setCompanyChannel] = useState('')
  const [subFolder, setSubFolder] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) {
      setForm(initial ?? EMPTY_FORM)
      const { companyChannel: cc, subFolder: sf } = splitFolder(initial?.folder)
      setCompanyChannel(cc)
      setSubFolder(sf)
      setError(null)
    }
  }, [open, initial])

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const combinedFolder = subFolder.trim()
    ? `${companyChannel.trim()}/${subFolder.trim()}`
    : companyChannel.trim()
  // Team (2026-10-07): the picker and drafter filter templates by team. Until
  // an admin picks one, it follows the folder tree (teamForFolder()).
  const team = form.team ?? teamForFolder(combinedFolder)

  async function handleSave() {
    setSaving(true); setError(null)
    try {
      let saved
      if (initial?.id) {
        saved = await adminTemplates.update(initial.id, {
          folder: combinedFolder,
          title: form.title,
          content: form.content,
          keywords: form.keywords || null,
          team,
        })
      } else {
        saved = await adminTemplates.create({
          folder: combinedFolder,
          title: form.title,
          content: form.content,
          keywords: form.keywords || null,
          team,
        })
      }
      onSaved(saved)
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const companyChannelOptions = [...new Set([
    ...(folders ?? []).map((f) => splitFolder(f).companyChannel).filter(Boolean),
    ...EMPTY_TEMPLATE_ROOTS,
  ])].sort()
  const subFolderOptions = [...new Set(
    (folders ?? [])
      .map((f) => splitFolder(f))
      .filter((s) => s.companyChannel === companyChannel && s.subFolder)
      .map((s) => s.subFolder)
  )].sort()

  const valid = companyChannel.trim() && form.title.trim() && form.content.trim()

  return (
    <DraggableModal
      open={open}
      onClose={() => !saving && onClose()}
      title={initial?.id ? 'Edit Template' : 'New Template'}
      width={700}
      height={640}
      minWidth={480}
      minHeight={420}
      actions={<>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !valid}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </>}
    >
      <Stack spacing={2} sx={{ mt: 0.5 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" spacing={1.5}>
          <Autocomplete
            freeSolo
            size="small"
            sx={{ flex: 1 }}
            options={companyChannelOptions}
            inputValue={companyChannel}
            disabled={saving}
            onInputChange={(_e, newValue) => setCompanyChannel(newValue)}
            onChange={(_e, newValue) => { setCompanyChannel(newValue ?? ''); setSubFolder('') }}
            renderInput={(params) => (
              <TextField {...params} label="Company & Channel" required placeholder="e.g. 1A eBay" />
            )}
          />
          <Autocomplete
            freeSolo
            size="small"
            sx={{ flex: 1 }}
            options={subFolderOptions}
            inputValue={subFolder}
            disabled={saving}
            onInputChange={(_e, newValue) => setSubFolder(newValue)}
            onChange={(_e, newValue) => setSubFolder(newValue ?? '')}
            renderInput={(params) => (
              <TextField {...params} label="Folder" placeholder="e.g. Refund" />
            )}
          />
        </Stack>
        <TextField
          label="Title"
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          required
          disabled={saving}
          fullWidth
          size="small"
        />
        <FormControl size="small" fullWidth disabled={saving}>
          <InputLabel>Team</InputLabel>
          <Select value={team} label="Team" onChange={(e) => set('team', e.target.value)} SelectDisplayProps={{ 'data-testid': 'template-team' }}>
            {TEAMS.map((t) => <MenuItem key={t} value={t}>{teamLabel(t)}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          label="Keywords"
          value={form.keywords}
          onChange={(e) => set('keywords', e.target.value)}
          disabled={saving}
          size="small"
          fullWidth
          placeholder="comma-separated"
        />
        <TemplateRichEditor
          label="Content"
          value={form.content}
          onChange={(html) => set('content', html)}
          disabled={saving}
          minRows={10}
        />
      </Stack>
    </DraggableModal>
  )
}

function DeleteConfirmDialog({ open, template, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => { if (open) setError(null) }, [open])

  async function handleDelete() {
    setDeleting(true); setError(null)
    try {
      await adminTemplates.delete(template.id)
      onDeleted(template.id)
    } catch (e) {
      setError(e.message)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Dialog open={open} onClose={() => !deleting && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Template?</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Typography>
          Delete <b>{template?.title}</b>? This cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Pencil icon
function EditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z" />
    </svg>
  )
}

// Trash icon
function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  )
}

// Chevron, rotated via CSS when expanded — no separate icon needed.
function ChevronIcon({ expanded }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

// Splits real folder names (e.g. "1A eBay/Tracking") into a one-level tree —
// no hardcoded "1A eBay" special case, no new DB column: any folder with a
// "/" becomes a collapsible parent node keyed off the part before the first
// slash, with its children keyed off the part after. Folders with no "/"
// render flat, same as before. Children are sorted; groups are sorted by
// their parent label, interleaved with flat folders alphabetically.
function buildFolderTree(folderNames) {
  const groups = new Map()
  const flat = []
  for (const name of folderNames) {
    const i = name.indexOf('/')
    if (i === -1) {
      flat.push(name)
      continue
    }
    const parent = name.slice(0, i)
    const child = name.slice(i + 1)
    if (!groups.has(parent)) groups.set(parent, [])
    groups.get(parent).push({ full: name, label: child })
  }
  for (const children of groups.values()) children.sort((a, b) => a.label.localeCompare(b.label))

  const nodes = [
    ...flat.map((name) => ({ type: 'flat', name })),
    ...[...groups.entries()].map(([parent, children]) => ({ type: 'group', parent, children })),
  ]
  nodes.sort((a, b) => (a.type === 'flat' ? a.name : a.parent).localeCompare(b.type === 'flat' ? b.name : b.parent))
  return nodes
}

export default function TemplatesPage() {
  const [grouped, setGrouped] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedFolder, setSelectedFolder] = useState(null)
  const [editDialog, setEditDialog] = useState({ open: false, initial: null })
  const [deleteDialog, setDeleteDialog] = useState({ open: false, template: null })
  const [search, setSearch] = useState('')
  // Multiple templates can be expanded simultaneously (was a single
  // expandedId). Reset to empty whenever the folder or search changes, so
  // templates always default to collapsed for a fresh view.
  const [expandedIds, setExpandedIds] = useState(new Set())
  // Tracks COLLAPSED group parents (e.g. "1A eBay") — empty set means every
  // group starts expanded, which also means a group is never collapsed out
  // from under its own currently-selected child on first render.
  const [collapsedGroups, setCollapsedGroups] = useState(new Set())

  const load = useCallback(() => {
    setLoading(true); setError(null)
    adminTemplates.list()
      .then((g) => {
        setGrouped(g)
        // No folder auto-selected on load — the page opens with nothing
        // highlighted (a global search across every folder) unless the
        // previously-selected folder still exists.
        setSelectedFolder((prev) => prev && g[prev] ? prev : null)
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const folders = Object.keys(grouped).sort()
  const templates = selectedFolder ? (grouped[selectedFolder] ?? []) : []
  // No folder selected: search scope is every template in every folder
  // (each still carries its own .folder field from the API, used to label
  // results below since there's no folder context on screen otherwise).
  const searchScope = selectedFolder ? templates : Object.values(grouped).flat()
  const visibleTemplates = searchScope
    .filter((t) => matchesTemplateSearch(t, search))
    .sort((a, b) => a.title.localeCompare(b.title))
  const folderTree = buildFolderTree(folders)

  const allVisibleExpanded = visibleTemplates.length > 0 && visibleTemplates.every((t) => expandedIds.has(t.id))

  function toggleExpanded(id) {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleExpandAll() {
    setExpandedIds(allVisibleExpanded ? new Set() : new Set(visibleTemplates.map((t) => t.id)))
  }

  function selectFolder(folder) {
    setSelectedFolder(folder)
    setExpandedIds(new Set())
  }

  function handleSearchChange(value) {
    setSearch(value)
    setExpandedIds(new Set())
  }

  function toggleGroup(parent) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(parent)) next.delete(parent)
      else next.add(parent)
      return next
    })
  }

  function handleSaved(saved) {
    setEditDialog({ open: false, initial: null })
    // Reload to get fresh grouped data
    load()
  }

  function handleDeleted(id) {
    setDeleteDialog({ open: false, template: null })
    setGrouped((prev) => {
      const next = { ...prev }
      for (const folder of Object.keys(next)) {
        next[folder] = next[folder].filter((t) => t.id !== id)
        if (next[folder].length === 0) delete next[folder]
      }
      return next
    })
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
      {/* Left: Folder sidebar */}
      <Box sx={{ width: 176, borderRight: 1, borderColor: 'divider', overflowY: 'auto', flexShrink: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>Folders</Typography>
          <Button size="small" onClick={() => setEditDialog({ open: true, initial: null })}>+ New</Button>
        </Stack>
        {loading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>
        ) : (
          <List dense disablePadding>
            {folders.length === 0 ? (
              <Box sx={{ px: 1.5, py: 1.25 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  No templates yet. Run the seed script to import the template library:
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all', color: 'text.secondary' }}
                >
                  pnpm --filter @cxhub/server seed-templates
                </Typography>
              </Box>
            ) : folderTree.map((node) => node.type === 'flat' ? (
              <ListItemButton
                key={node.name}
                selected={node.name === selectedFolder}
                onClick={() => selectFolder(node.name)}
                sx={{ px: 1.5, py: 0.5 }}
              >
                <ListItemText
                  primary={node.name}
                  primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>
                  {(grouped[node.name] ?? []).length}
                </Typography>
              </ListItemButton>
            ) : (
              <Box key={node.parent}>
                <ListItemButton onClick={() => toggleGroup(node.parent)} sx={{ px: 1.5, py: 0.5 }}>
                  <Box sx={{ display: 'flex', mr: 0.5, color: 'text.secondary' }}>
                    <ChevronIcon expanded={!collapsedGroups.has(node.parent)} />
                  </Box>
                  <ListItemText
                    primary={node.parent}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600, noWrap: true }}
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                </ListItemButton>
                {!collapsedGroups.has(node.parent) && node.children.map((c) => (
                  <ListItemButton
                    key={c.full}
                    selected={c.full === selectedFolder}
                    onClick={() => selectFolder(c.full)}
                    sx={{ px: 1.5, py: 0.5, pl: 3.5 }}
                  >
                    <ListItemText
                      primary={c.label}
                      primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                      sx={{ flex: 1, minWidth: 0 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>
                      {(grouped[c.full] ?? []).length}
                    </Typography>
                  </ListItemButton>
                ))}
              </Box>
            ))}
          </List>
        )}
      </Box>

      {/* Right: Template list — a folder scopes the search to itself; with
          none selected (the default landing state), search spans every
          folder and each result is labeled with its own folder. */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {!loading && (
          <>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <Typography variant="h6" fontWeight={700} sx={{ flex: 1, minWidth: 120 }}>
                {selectedFolder ?? 'All Templates'}
              </Typography>
              <Chip size="small" label={`${visibleTemplates.length} templates`} />
              {visibleTemplates.length > 0 && (
                <Button size="small" onClick={toggleExpandAll}>
                  {allVisibleExpanded ? 'Collapse All' : 'Expand All'}
                </Button>
              )}
              {selectedFolder && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setEditDialog({ open: true, initial: { folder: selectedFolder, title: '', content: '', keywords: '', team: null } })}
                >
                  + Add Template
                </Button>
              )}
            </Stack>

            <TextField
              size="small"
              fullWidth
              placeholder={selectedFolder ? `Search ${selectedFolder}...` : 'Search all templates by title or content...'}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Stack spacing={1}>
              {visibleTemplates.map((t) => (
                <Paper key={t.id} variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ flex: 1, cursor: 'pointer' }}
                      onClick={() => toggleExpanded(t.id)}
                    >
                      {t.title}
                    </Typography>
                    {!selectedFolder && (
                      <Chip size="small" variant="outlined" label={t.folder} sx={{ flexShrink: 0 }} />
                    )}
                    {t.keywords && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                        {t.keywords}
                      </Typography>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => setEditDialog({ open: true, initial: t })}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteDialog({ open: true, template: t })}
                      title="Delete"
                    >
                      <TrashIcon />
                    </IconButton>
                  </Stack>
                  {expandedIds.has(t.id) && (
                    <>
                      <Divider />
                      <Box sx={{ p: 1.5, fontSize: '0.875rem', maxHeight: 300, overflowY: 'auto', ...PREVIEW_PARAGRAPH_SX }}>
                        <div dangerouslySetInnerHTML={{ __html: previewHtml(t.content) }} />
                      </Box>
                    </>
                  )}
                </Paper>
              ))}
              {visibleTemplates.length === 0 && (
                <Typography color="text.secondary" variant="body2">
                  {search.trim()
                    ? 'No templates match your search.'
                    : selectedFolder
                      ? 'No templates in this folder.'
                      : 'No templates yet.'}
                </Typography>
              )}
            </Stack>
          </>
        )}
      </Box>

      <TemplateDialog
        open={editDialog.open}
        initial={editDialog.initial}
        folders={folders}
        onClose={() => setEditDialog({ open: false, initial: null })}
        onSaved={handleSaved}
      />

      {deleteDialog.template && (
        <DeleteConfirmDialog
          open={deleteDialog.open}
          template={deleteDialog.template}
          onClose={() => setDeleteDialog({ open: false, template: null })}
          onDeleted={handleDeleted}
        />
      )}
    </Box>
  )
}
